import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Eye, EyeOff, Loader2 } from 'lucide-react';
import { getAggregatorPortfolioProjects, setAnalystProjectVisibility, getAnalystProjectVisibility } from '../../services/api';
import type { AggregatorTeamMember } from '../../types';
import { useAggregator } from '../../contexts/AggregatorContext';

interface Props {
  analyst: AggregatorTeamMember;
  onClose: () => void;
  onSuccess: () => void;
}

export function AnalystVisibilityEditor({ analyst, onClose, onSuccess }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // hiddenIds tracks which projects the analyst CANNOT see
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());
  const [visibilityLoaded, setVisibilityLoaded] = useState(false);
  const { activeAggregatorId } = useAggregator();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['aggregator-portfolio-projects', activeAggregatorId],
    queryFn: () => getAggregatorPortfolioProjects(activeAggregatorId),
    staleTime: 60_000,
  });

  // BUG-017: load the analyst's CURRENT hidden project ids and seed state from
  // them. The Save handler PUTs the FULL desired hidden set and the backend
  // diffs it against what is stored — so if we started from an empty set (the
  // old behaviour), a Save (even a no-op) would remove every existing exclusion,
  // silently un-hiding all previously hidden projects. Seeding from the real
  // stored set makes a no-op Save a genuine no-op.
  const {
    data: visibilityData,
    isLoading: visibilityLoading,
    isError: visibilityError,
  } = useQuery({
    queryKey: ['analyst-project-visibility', analyst.member_user_id],
    queryFn: () => getAnalystProjectVisibility(analyst.member_user_id),
  });

  useEffect(() => {
    if (visibilityData && !visibilityLoaded) {
      setHiddenIds(new Set(visibilityData.hidden_project_ids ?? []));
      setVisibilityLoaded(true);
    }
  }, [visibilityData, visibilityLoaded]);

  // Group projects by entity for display
  const groupedByEntity: Record<string, { entityName: string; projects: { id: string; name: string }[] }> = {};
  if (data) {
    for (const entity of data.entities ?? []) {
      groupedByEntity[entity.entity_id] = { entityName: entity.entity_name, projects: [] };
    }
    for (const project of data.projects ?? []) {
      const entityId = project.entity_id ?? 'uncategorised';
      if (!groupedByEntity[entityId]) {
        groupedByEntity[entityId] = { entityName: entityId, projects: [] };
      }
      groupedByEntity[entityId].projects.push({ id: project.project_id, name: project.name });
    }
  }

  const allProjectIds = data?.projects?.map(p => p.project_id) ?? [];
  const visibleCount = allProjectIds.length - hiddenIds.size;

  const toggleProject = (id: string) => {
    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const tickAll = () => setHiddenIds(new Set());
  const untickAll = () => setHiddenIds(new Set(allProjectIds));

  const handleSave = async () => {
    // Never save against an unknown baseline — that is exactly the state that
    // silently un-hides everything (BUG-017).
    if (!visibilityLoaded) return;
    setSaving(true);
    setError(null);
    try {
      await setAnalystProjectVisibility(analyst.member_user_id, Array.from(hiddenIds));
      onSuccess();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save visibility.');
    } finally {
      setSaving(false);
    }
  };

  const displayName = analyst.member_name ?? analyst.member_email;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Project Visibility</h2>
            <p className="text-sm text-gray-500 mt-0.5">{displayName}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {(isLoading || visibilityLoading) && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
            </div>
          )}

          {isError && (
            <p className="text-sm text-red-600">Failed to load projects. Close and retry.</p>
          )}

          {visibilityError && !visibilityLoading && (
            <p className="text-sm text-red-600">
              Could not load this analyst&apos;s current visibility settings. Saving is disabled
              to avoid accidentally clearing existing restrictions. Close and retry.
            </p>
          )}

          {data && visibilityLoaded && (
            <>
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-gray-600">
                  <strong className="text-gray-900">{visibleCount}</strong> of {allProjectIds.length} projects visible
                </p>
                <div className="flex gap-3 text-xs">
                  <button onClick={tickAll} className="flex items-center gap-1 text-indigo-600 hover:text-indigo-800">
                    <Eye className="w-3.5 h-3.5" /> Show all
                  </button>
                  <button onClick={untickAll} className="flex items-center gap-1 text-gray-500 hover:text-gray-700">
                    <EyeOff className="w-3.5 h-3.5" /> Hide all
                  </button>
                </div>
              </div>

              <div className="space-y-5">
                {Object.entries(groupedByEntity).map(([entityId, group]) => (
                  group.projects.length > 0 && (
                    <div key={entityId}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                        {group.entityName}
                      </p>
                      <div className="space-y-1.5">
                        {group.projects.map(project => {
                          const visible = !hiddenIds.has(project.id);
                          return (
                            <label
                              key={project.id}
                              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={visible}
                                onChange={() => toggleProject(project.id)}
                                className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                              />
                              <span className={`text-sm ${visible ? 'text-gray-800' : 'text-gray-500 line-through'}`}>
                                {project.name}
                              </span>
                              {visible
                                ? <Eye className="w-3.5 h-3.5 text-indigo-400 ml-auto" />
                                : <EyeOff className="w-3.5 h-3.5 text-gray-300 ml-auto" />}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 space-y-3">
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || isLoading || visibilityLoading || !visibilityLoaded}
              className="flex-1 py-2 px-4 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : 'Save Visibility'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
